/**
 * Agentic Evaluation View — displays agentic evaluation results.
 *
 * Features:
 * - Radar chart for 4-dimension scores (0-100)
 * - Hiring decision badge (STRONG_HIRE, HIRE, BORDERLINE, REJECT)
 * - "The Diff" — side-by-side comparison of user answer vs Staff Engineer rewrite
 * - Red flags / missing components list
 * - Summary feedback
 *
 * This component renders within the existing EvaluationDetail page
 * when evaluation_type === "agentic".
 */

import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme, type Theme } from "@mui/material/styles";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StarIcon from "@mui/icons-material/Star";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { Evaluation } from "../api/client";

// ---------------------------------------------------------------------------
// Dimension display names
// ---------------------------------------------------------------------------

function behavioralDimensions(t: TFunction): Record<string, string> {
  return {
    agentic_thinking: t("AgenticEvaluationView.dimension.agenticThinking"),
    safety_alignment: t("AgenticEvaluationView.dimension.safetyAlignment"),
    engineering_rigor: t("AgenticEvaluationView.dimension.engineeringRigor"),
    communication: t("AgenticEvaluationView.dimension.communication"),
  };
}

function systemDesignDimensions(t: TFunction): Record<string, string> {
  return {
    requirements_clarity: t("AgenticEvaluationView.dimension.requirementsClarity"),
    architecture_soundness: t("AgenticEvaluationView.dimension.architectureSoundness"),
    agentic_patterns: t("AgenticEvaluationView.dimension.agenticPatterns"),
    safety_security: t("AgenticEvaluationView.dimension.safetySecurity"),
  };
}

function getDimensionLabels(
  scores: Record<string, number>,
  t: TFunction
): Record<string, string> {
  const keys = Object.keys(scores);
  if (keys.includes("agentic_thinking")) return behavioralDimensions(t);
  if (keys.includes("requirements_clarity")) return systemDesignDimensions(t);
  // Fallback: use the key itself, title-cased (dynamic, not translatable copy)
  const result: Record<string, string> = {};
  for (const key of keys) {
    result[key] = key
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return result;
}

// ---------------------------------------------------------------------------
// Score color logic (0-100 scale)
// ---------------------------------------------------------------------------

function getScoreColor100(score: number, theme: Theme): string {
  if (score >= 80) return theme.palette.success.main; // Green — strong
  if (score >= 65) return theme.palette.warning.main; // Amber — solid
  if (score >= 50) return theme.palette.warning.dark; // Orange — borderline
  return theme.palette.error.main; // Red — weak
}

function getScoreLabel100(score: number, t: TFunction): string {
  if (score >= 90) return t("AgenticEvaluationView.scoreLabel.exceptional");
  if (score >= 80) return t("AgenticEvaluationView.scoreLabel.strong");
  if (score >= 70) return t("AgenticEvaluationView.scoreLabel.solid");
  if (score >= 60) return t("AgenticEvaluationView.scoreLabel.average");
  if (score >= 50) return t("AgenticEvaluationView.scoreLabel.belowAvg");
  return t("AgenticEvaluationView.scoreLabel.weak");
}

// ---------------------------------------------------------------------------
// Hiring decision styling
// ---------------------------------------------------------------------------

function getHiringDecisionStyle(
  decision: string,
  theme: Theme
): {
  color: string;
  bgcolor: string;
  icon: React.ReactNode;
} {
  switch (decision) {
    case "STRONG_HIRE":
      return {
        color: theme.palette.success.dark,
        bgcolor: alpha(theme.palette.success.dark, 0.2),
        icon: <StarIcon sx={{ color: theme.palette.success.dark }} />,
      };
    case "HIRE":
      return {
        color: theme.palette.success.dark,
        bgcolor: alpha(theme.palette.success.main, 0.1),
        icon: <CheckCircleOutlineIcon sx={{ color: theme.palette.success.dark }} />,
      };
    case "BORDERLINE":
      return {
        color: theme.palette.warning.dark,
        bgcolor: alpha(theme.palette.warning.dark, 0.1),
        icon: <WarningAmberIcon sx={{ color: theme.palette.warning.dark }} />,
      };
    case "REJECT":
      return {
        color: theme.palette.error.dark,
        bgcolor: alpha(theme.palette.error.main, 0.08),
        icon: <ErrorOutlineIcon sx={{ color: theme.palette.error.dark }} />,
      };
    default:
      return {
        color: theme.palette.text.secondary,
        bgcolor: theme.palette.background.default,
        icon: null,
      };
  }
}

// ---------------------------------------------------------------------------
// Score Bar for 0-100 scale
// ---------------------------------------------------------------------------

function AgenticScoreBar({
  label,
  score,
}: {
  label: string;
  score: number;
}) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const color = getScoreColor100(score, theme);
  const scoreLabel = getScoreLabel100(score, t);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500} sx={{ color }}>
          {t("AgenticEvaluationView.scoreOutOf100", { score, label: scoreLabel })}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: "grey.200",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${score}%`,
            borderRadius: 5,
            bgcolor: color,
            transition: "width 0.8s ease",
          }}
        />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CSS Radar Chart (no charting library needed)
// ---------------------------------------------------------------------------

function RadarChart({
  scores,
  labels,
}: {
  scores: Record<string, number>;
  labels: Record<string, string>;
}) {
  const theme = useTheme();
  const dimensions = Object.keys(scores);
  const n = dimensions.length;
  if (n < 3) return null;

  const size = 200;
  const center = size / 2;
  const maxRadius = size / 2 - 20;

  // Calculate polygon points for the score shape
  const scorePoints = dimensions.map((dim, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    const radius = (scores[dim] / 100) * maxRadius;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
    };
  });

  // Grid rings at 25%, 50%, 75%, 100%
  const rings = [0.25, 0.5, 0.75, 1.0];

  // Axis lines
  const axes = dimensions.map((_, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    return {
      x2: center + maxRadius * Math.cos(angle),
      y2: center + maxRadius * Math.sin(angle),
    };
  });

  // Label positions (slightly outside the chart)
  const labelPositions = dimensions.map((dim, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    const labelRadius = maxRadius + 16;
    return {
      x: center + labelRadius * Math.cos(angle),
      y: center + labelRadius * Math.sin(angle),
      label: labels[dim] || dim,
      score: scores[dim],
    };
  });

  const scorePolygon = scorePoints.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <Box sx={{ textAlign: "center", my: 2 }}>
      <svg
        viewBox={`-30 -30 ${size + 60} ${size + 60}`}
        width="100%"
        style={{ maxWidth: 300 }}
      >
        {/* Grid rings */}
        {rings.map((r) => {
          const ringPoints = dimensions.map((_, i) => {
            const angle = (2 * Math.PI * i) / n - Math.PI / 2;
            const radius = r * maxRadius;
            return `${center + radius * Math.cos(angle)},${
              center + radius * Math.sin(angle)
            }`;
          });
          return (
            <polygon
              key={r}
              points={ringPoints.join(" ")}
              fill="none"
              stroke={theme.palette.divider}
              strokeWidth={0.5}
            />
          );
        })}

        {/* Axis lines */}
        {axes.map((axis, i) => (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={axis.x2}
            y2={axis.y2}
            stroke={theme.palette.divider}
            strokeWidth={0.5}
          />
        ))}

        {/* Score polygon — uses theme navy */}
        <polygon
          points={scorePolygon}
          fill={alpha(theme.palette.text.primary, 0.15)}
          stroke={theme.palette.text.primary}
          strokeWidth={2}
          strokeLinejoin="round"
        />

        {/* Score dots */}
        {scorePoints.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={4}
            fill={theme.palette.text.primary}
            stroke={theme.palette.background.paper}
            strokeWidth={1.5}
          />
        ))}

        {/* Labels */}
        {labelPositions.map((lp, i) => (
          <text
            key={i}
            x={lp.x}
            y={lp.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={9}
            fontFamily="system-ui, sans-serif"
            fill={theme.palette.text.secondary}
          >
            <tspan x={lp.x} dy="-0.5em" fontWeight={500}>
              {lp.label}
            </tspan>
            <tspan
              x={lp.x}
              dy="1.2em"
              fill={getScoreColor100(lp.score, theme)}
              fontWeight={500}
            >
              {lp.score}
            </tspan>
          </text>
        ))}
      </svg>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

interface AgenticEvaluationViewProps {
  evaluation: Evaluation;
}

export default function AgenticEvaluationView({
  evaluation,
}: AgenticEvaluationViewProps) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const scores = evaluation.agentic_scores;
  const result = evaluation.agentic_result;

  if (!scores || !result) {
    return (
      <Alert severity="warning">
        {t("AgenticEvaluationView.unavailable")}
      </Alert>
    );
  }

  const labels = getDimensionLabels(scores, t);
  const avgScore = Math.round(
    Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length
  );
  const hiringDecision = evaluation.hiring_decision || result.hiring_decision || "—";
  const decisionStyle = getHiringDecisionStyle(hiringDecision, theme);

  const feedback = result.summary_feedback || result.feedback_summary || "";
  const redFlags = result.red_flags || [];
  const missingComponents = result.missing_components || [];
  const theDiff = result.the_diff || {};

  return (
    <Grid container spacing={3}>
      {/* Left Panel — Scores + Decision */}
      <Grid item xs={12} md={4}>
        <Card sx={{ position: "sticky", top: 80 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("AgenticEvaluationView.scores")}
            </Typography>

            {/* Hiring Decision Badge */}
            <Paper
              elevation={0}
              sx={{
                textAlign: "center",
                py: 2.5,
                mb: 2.5,
                bgcolor: decisionStyle.bgcolor,
                borderRadius: 2,
                border: 1,
                borderColor: decisionStyle.color,
                borderStyle: "solid",
                opacity: 0.95,
              }}
            >
              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                {decisionStyle.icon}
                <Typography
                  variant="h5"
                  fontWeight={500}
                  sx={{ color: decisionStyle.color, letterSpacing: "-0.01em" }}
                >
                  {hiringDecision.replace(/_/g, " ")}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ color: decisionStyle.color, mt: 0.5, fontWeight: 500 }}
              >
                {t("AgenticEvaluationView.avgScore", { avg: avgScore })}
              </Typography>
            </Paper>

            {/* Radar Chart */}
            <RadarChart scores={scores} labels={labels} />

            <Divider sx={{ my: 2 }} />

            {/* Score Bars */}
            {Object.entries(scores).map(([dim, score]) => (
              <AgenticScoreBar
                key={dim}
                label={labels[dim] || dim}
                score={score}
              />
            ))}
          </CardContent>
        </Card>

        {/* Red Flags */}
        {redFlags.length > 0 && (
          <Card sx={{ mt: 2, border: 1, borderColor: "error.light" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <ErrorOutlineIcon color="error" />
                <Typography variant="h6" color="error.dark">
                  {t("AgenticEvaluationView.redFlags")}
                </Typography>
              </Stack>
              {redFlags.map((flag, i) => (
                <Alert
                  key={i}
                  severity="error"
                  variant="outlined"
                  sx={{ mb: 1, "&:last-child": { mb: 0 } }}
                >
                  {flag}
                </Alert>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Missing Components (system design) */}
        {missingComponents.length > 0 && (
          <Card sx={{ mt: 2, border: 1, borderColor: "warning.light" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <WarningAmberIcon color="warning" />
                <Typography variant="h6" color="warning.dark">
                  {t("AgenticEvaluationView.missingComponents")}
                </Typography>
              </Stack>
              {missingComponents.map((comp, i) => (
                <Alert
                  key={i}
                  severity="warning"
                  variant="outlined"
                  sx={{ mb: 1, "&:last-child": { mb: 0 } }}
                >
                  {comp}
                </Alert>
              ))}
            </CardContent>
          </Card>
        )}
      </Grid>

      {/* Right Panel — Feedback + The Diff */}
      <Grid item xs={12} md={8}>
        {/* Summary Feedback */}
        {feedback && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("AgenticEvaluationView.feedback")}
              </Typography>
              <Typography variant="body1">{feedback}</Typography>
            </CardContent>
          </Card>
        )}

        {/* THE DIFF — the hero feature */}
        <Card
          sx={{
            mb: 2,
            border: 2,
            borderColor: "primary.main",
            overflow: "hidden",
          }}
        >
          {/* Diff header band */}
          <Box
            sx={{
              px: 3,
              py: 2,
              background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.info.main} 100%)`,
              color: "white",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h5" fontWeight={500} sx={{ letterSpacing: "-0.01em" }}>
                {t("AgenticEvaluationView.theDiff")}
              </Typography>
              <Chip
                label={t("AgenticEvaluationView.keyTakeaway")}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 500,
                  height: 24,
                }}
              />
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
              {t("AgenticEvaluationView.diffSubtitle")}
            </Typography>
          </Box>
          <CardContent sx={{ pt: 2.5 }}>

            <Grid container spacing={2}>
              {/* User's Answer / Critique */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.warning.dark, 0.08),
                    borderRadius: 2,
                    height: "100%",
                    borderLeft: 4,
                    borderColor: theme.palette.warning.dark,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    color={theme.palette.warning.dark}
                    gutterBottom
                  >
                    {theDiff.user_answer_critique
                      ? t("AgenticEvaluationView.yourAnswerCritique")
                      : t("AgenticEvaluationView.yourApproach")}
                  </Typography>
                  <Typography variant="body2">
                    {theDiff.user_answer_critique ||
                      theDiff.candidate_approach ||
                      t("AgenticEvaluationView.noCritique")}
                  </Typography>
                </Paper>
              </Grid>

              {/* Staff Engineer Rewrite */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    borderRadius: 2,
                    height: "100%",
                    borderLeft: 4,
                    borderColor: theme.palette.success.dark,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    color={theme.palette.success.dark}
                    gutterBottom
                  >
                    {theDiff.staff_engineer_rewrite
                      ? t("AgenticEvaluationView.staffEngineerAnswer")
                      : t("AgenticEvaluationView.staffArchitectApproach")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {theDiff.staff_engineer_rewrite ||
                      theDiff.staff_architect_approach ||
                      t("AgenticEvaluationView.noRewrite")}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Collapsible: View My Original Answer */}
        {evaluation.answer_text && (
          <OriginalAnswerSection answerText={evaluation.answer_text} />
        )}
      </Grid>
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// Original Answer — collapsible comparison
// ---------------------------------------------------------------------------

function OriginalAnswerSection({ answerText }: { answerText: string }) {
  const { t } = useTranslation("common");
  const [expanded, setExpanded] = useState(false);

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Button
          fullWidth
          onClick={() => setExpanded(!expanded)}
          startIcon={<PersonIcon />}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          }
          sx={{
            justifyContent: "space-between",
            textTransform: "none",
            fontWeight: 500,
            color: "text.primary",
          }}
        >
          {t("AgenticEvaluationView.viewOriginalAnswer")}
        </Button>
        <Collapse in={expanded}>
          <Paper
            elevation={0}
            sx={{
              mt: 1.5,
              p: 2,
              bgcolor: "action.hover",
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {answerText}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              {t("AgenticEvaluationView.wordCount", {
                count: answerText.trim().split(/\s+/).length,
              })}
            </Typography>
          </Paper>
        </Collapse>
      </CardContent>
    </Card>
  );
}
